import { AccountView, store } from '../store';
import { useState, useEffect } from 'preact/hooks';
import { t } from '../i18n';
import { navigateToView } from '../navigation';

const IconCart = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
  </svg>
);

const IconCard = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H0zm0 3v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7zm3 2h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1"/>
  </svg>
);

const IconMedical = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/>
    <path fill-rule="evenodd" d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5"/>
  </svg>
);

const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
  </svg>
);

const IconDone = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
  </svg>
);

const STEPS = [
  { id: 'cart',           labelKey: 'stepper.cart',    Icon: IconCart },
  { id: 'checkout',       labelKey: 'stepper.payment', Icon: IconCard },
  { id: 'checkout-qnr',   labelKey: 'stepper.medical', Icon: IconMedical },
  { id: 'checkout-confirm', labelKey: 'stepper.confirm', Icon: IconCheck },
];

export function CheckoutStepper({ currentStep }: { currentStep: AccountView }) {
  const [settings, setSettings] = useState<any>(store.getSettings());

  useEffect(() => {
    return store.subscribe(() => setSettings(store.getSettings()));
  }, []);

  const qnrEnabled = settings.QNR !== 'N';
  const steps = STEPS.filter(s => s.id !== 'checkout-qnr' || qnrEnabled);
  const currentIndex = steps.findIndex(s => s.id === currentStep);
  const progressWidth = steps.length > 1 ? `${(currentIndex / (steps.length - 1)) * 100}%` : '0%';
  const medallionSize = 40;
  const trackInset = '20px';
  const trackTop = `${medallionSize / 2}px`;

  if (currentIndex === -1) return null;

  const handleStepClick = (stepId: AccountView, isCompleted: boolean) => {
    if (!isCompleted || !stepId) return;
    navigateToView(stepId);
  };

  return (
    <div class="checkout-stepper mb-4 px-3">
      <div class="position-relative">
        <div
          class="position-absolute"
          style={{
            left: trackInset,
            right: trackInset,
            top: trackTop,
            height: '2px',
            transform: 'translateY(-50%)',
            background: 'rgba(0,0,0,0.10)',
            zIndex: 0,
          }}
        ></div>
        <div
          class="position-absolute start-0 bg-success"
          style={{
            left: trackInset,
            top: trackTop,
            height: '2px',
            transform: 'translateY(-50%)',
            zIndex: 0,
            width: `calc((100% - (${trackInset} * 2)) * ${progressWidth.replace('%', '')} / 100)`,
            transition: 'width 0.3s ease',
          }}
        ></div>
        <div
          class="checkout-stepper__grid"
          style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, alignItems: 'start', position: 'relative', zIndex: 1 }}
        >

          {steps.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent   = index === currentIndex;
            const { Icon }    = step;
            const clickable   = isCompleted;

            return (
              <div
                key={step.id}
                class="d-flex flex-column align-items-center text-center"
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : -1}
                onClick={clickable ? () => handleStepClick(step.id as AccountView, true) : undefined}
                onKeyDown={clickable ? (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStepClick(step.id as AccountView, true); } } : undefined}
                style={{ minWidth: 0, cursor: clickable ? 'pointer' : 'default' }}
                title={clickable ? t(step.labelKey) : undefined}
              >
                <div
                  class={`rounded-circle d-flex align-items-center justify-content-center border border-2 ${
                    isCompleted || isCurrent ? 'bg-success border-success text-white' : 'bg-white border-secondary text-secondary'
                  }`}
                  style={{ width: `${medallionSize}px`, height: `${medallionSize}px`, transition: 'all 0.3s ease', margin: '0 auto' }}
                >
                  {isCompleted ? <IconDone /> : <Icon />}
                </div>
                <div
                  class={`small mt-1 fw-bold ${isCompleted || isCurrent ? 'text-success' : 'text-secondary'}`}
                  style={{ width: '100%', textAlign: 'center', lineHeight: '1.25', padding: '0 .25rem' }}
                >
                  {t(step.labelKey)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
